/**
 * Attempts to get the actual content height of an iframe
 */
const getIframeContentHeight = (iframe: HTMLIFrameElement): number | null => {
  try {
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (iframeDoc) {
      const body = iframeDoc.body;
      const html = iframeDoc.documentElement;

      const height = Math.max(
        body?.scrollHeight || 0,
        body?.offsetHeight || 0,
        html?.clientHeight || 0,
        html?.scrollHeight || 0,
        html?.offsetHeight || 0,
      );

      return height > 0 ? height : null;
    }
  } catch (error) {
    console.debug("Cannot access iframe content (cross-origin):", error);
  }
  return null;
};

/**
 * Sets up dynamic height adjustment for an ad iframe
 */
const setupDynamicIframeHeight = (
  iframe: HTMLIFrameElement,
  container: HTMLDivElement,
  id: string,
  fallbackHeight: string | number,
): (() => void) => {
  let resizeObserver: ResizeObserver | null = null;
  let timeoutId: NodeJS.Timeout | null = null;

  const adjustHeight = () => {
    const contentHeight = getIframeContentHeight(iframe);

    if (contentHeight && contentHeight > 0) {
      iframe.style.height = `${contentHeight}px`;
      container.style.height = `${contentHeight}px`;
      console.debug(
        `[Ad Height] Dynamic adjustment for ${id}: ${contentHeight}px`,
      );
    } else {
      setupContentLoadDetection(iframe, container, id, fallbackHeight);
    }
  };

  timeoutId = setTimeout(adjustHeight, 100);

  if (typeof ResizeObserver !== "undefined") {
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === iframe) {
          adjustHeight();
          break;
        }
      }
    });
    resizeObserver.observe(iframe);
  }

  return () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (resizeObserver) {
      resizeObserver.disconnect();
    }
  };
};

/**
 * Sets up content load detection for cross-origin iframes
 */
const setupContentLoadDetection = (
  iframe: HTMLIFrameElement,
  container: HTMLDivElement,
  id: string,
  fallbackHeight: string | number,
): void => {
  let attempts = 0;
  const maxAttempts = 5;

  const checkForContent = () => {
    attempts++;

    const iframeRect = iframe.getBoundingClientRect();
    const hasContent = iframeRect.height > 0 && iframeRect.width > 0;

    if (hasContent) {
      const minHeight = Math.max(325, iframeRect.height);
      iframe.style.height = `${minHeight}px`;
      container.style.height = `${minHeight}px`;
      console.debug(
        `[Ad Height] Content load detection for ${id}: ${minHeight}px`,
      );
    } else if (attempts < maxAttempts) {
      setTimeout(checkForContent, 200);
    } else {
      const finalHeight =
        typeof fallbackHeight === "string"
          ? fallbackHeight
          : `${fallbackHeight}px`;
      iframe.style.height = finalHeight;
      container.style.height = finalHeight;
      console.warn(
        `[Ad Height] Using fallback height for ${id}: ${finalHeight}`,
      );
    }
  };

  setTimeout(checkForContent, 100);
};

/**
 * Enhanced iframe setup with dynamic height adjustment
 */
const setupIframeWithDynamicHeight = (
  iframe: HTMLIFrameElement,
  container: HTMLDivElement,
  id: string,
  initialWidth: string,
  initialHeight: string,
  isFSS: boolean,
  FSSWidth: string,
  FSSHeight: string,
): (() => void) => {
  iframe.style.width = isFSS ? FSSWidth : initialWidth;
  iframe.title = "Xandr ad";

  if (id?.includes("takeover") && !isFSS) return () => {};

  iframe.style.height = isFSS ? FSSHeight : initialHeight;

  if (!isFSS || (isFSS && initialHeight === "100%")) {
    return setupDynamicIframeHeight(iframe, container, id, initialHeight);
  }

  return () => {}; // No cleanup needed
};

export {
  getIframeContentHeight,
  setupDynamicIframeHeight,
  setupContentLoadDetection,
  setupIframeWithDynamicHeight,
};
